<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { groupBy } from "lodash-es";
import { computed, ref, watch } from "vue";
import { useIntervalFn } from "@vueuse/core";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Account, Company, RegistryDevice } from "@/types";
import CnmcFilingDetailsDialog from "./CnmcFilingDetailsDialog.vue";
import CnmcDialog from "./CnmcDialog.vue";
import PrepareIssuanceWizard from "./PrepareIssuanceWizard.vue";
import CnmcIssuanceBatchDetail from "./CnmcIssuanceBatchDetail.vue";
import { cnmcApi } from "./cnmcApi";
import { useCnmcIssuanceStore } from "./useCnmcIssuanceStore";
import type {
  CnmcContext,
  CnmcDevice,
  CnmcFilingProfile,
  CnmcPlan,
} from "./cnmcTypes";
import CnmcNotice from "./CnmcNotice.vue";
import CnmcPlanProgress from "./CnmcPlanProgress.vue";
import {
  issuanceFileCount,
  planPeriod,
  reportedProduction,
} from "./cnmcPresentation";

const props = defineProps<{
  company: Company;
  account?: Account | null;
  registryDevices: RegistryDevice[];
  initialContext: CnmcContext;
  canImport: boolean;
  isAdmin?: boolean;
}>();
const { globalErrorHandler } = useGlobalHandlers();
const { locale } = useI18n();
const store = useCnmcIssuanceStore();
const context = ref(props.initialContext);
const connectionId = ref<number | null>(
  context.value.connections.find(
    (item) => item.active && item.preparation_enabled
  )?.id ??
    context.value.connections[0]?.id ??
    null
);
const devices = ref<CnmcDevice[]>([]);
const selected = ref<CnmcPlan | null>(null);
const editing = ref<CnmcPlan>();
const showWizard = ref(false);
const showProfile = ref(false);
const busy = ref(false);
const showBatch = computed({
  get: () => Boolean(selected.value) && !showWizard.value && !showProfile.value,
  set: (visible) => {
    if (!visible) {
      selected.value = null;
      void refresh();
    }
  },
});
const offset = ref(0);
const companyId = computed(() => props.company.id ?? 0);
const connection = computed(() =>
  context.value.connections.find((item) => item.id === connectionId.value)
);
const canPrepare = computed(() =>
  Boolean(
    context.value.preparation_enabled &&
    connection.value?.active &&
    connection.value?.preparation_enabled
  )
);
const templateNotices = computed(() =>
  canPrepare.value
    ? Object.entries(
        groupBy(
          store.templates.filter((item) => item.status !== "READY"),
          (item) =>
            item.status === "UPDATED"
              ? "template_updated"
              : item.error
                ? "template_check_failed"
                : item.status === "CHECKING"
                  ? "checking_templates"
                  : "template_verification_pending"
        )
      )
    : []
);
const refresh = async () => {
  if (connectionId.value === null) return;
  try {
    const company = companyId.value;
    const connection = connectionId.value;
    const planId = selected.value?.id;
    await store.load(company, connection, offset.value);
    if (planId) {
      const response = await cnmcApi.plan(company, connection, planId);
      if (
        company === companyId.value &&
        connection === connectionId.value &&
        selected.value?.id === planId
      )
        selected.value = response.data;
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};
watch(
  connectionId,
  async (id, _previous, onCleanup) => {
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });
    store.reset();
    devices.value = [];
    selected.value = null;
    showWizard.value = false;
    offset.value = 0;
    if (id === null) return;
    try {
      const response = await cnmcApi.devices(companyId.value, id);
      if (cancelled) return;
      devices.value = response.data.devices;
      await refresh();
    } catch (error) {
      globalErrorHandler(error);
    }
  },
  { immediate: true }
);
useIntervalFn(() => {
  if (!busy.value && !showWizard.value && !showProfile.value) void refresh();
}, 30000);
const saveProfile = async (profile: CnmcFilingProfile) => {
  if (connectionId.value === null) return;
  busy.value = true;
  try {
    const response = await cnmcApi.saveProfile(
      companyId.value,
      connectionId.value,
      profile
    );
    context.value = {
      ...context.value,
      connections: context.value.connections.map((item) =>
        item.id === connectionId.value
          ? { ...item, profile: response.data }
          : item
      ),
    };
    showProfile.value = false;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    busy.value = false;
  }
};
const prepare = () => {
  editing.value = undefined;
  selected.value = null;
  showWizard.value = true;
};
const edit = (plan: CnmcPlan) => {
  editing.value = plan;
  showWizard.value = true;
};
const open = (plan: CnmcPlan) => {
  selected.value = plan;
  showWizard.value = false;
};
const page = async (change: number) => {
  offset.value = Math.max(0, offset.value + change);
  await refresh();
};
const deviceNames = (plan: CnmcPlan) =>
  [
    ...new Set(
      plan.inputs.production_sources.map((source) => source.device_name)
    ),
  ].join(", ");
const recipients = (plan: CnmcPlan, route: "DOMESTIC" | "EXPORT") =>
  [
    ...new Set(
      plan.allocations
        .filter((allocation) => allocation.route === route)
        .map((allocation) => allocation.recipient_snapshot.name)
    ),
  ].join(", ");
</script>

<template>
  <div>
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4"
    >
      <div class="d-flex flex-wrap align-items-center gap-3">
        <button
          class="btn btn-primary"
          :disabled="!canPrepare || !connection?.profile"
          @click="prepare"
        >
          {{ $t("cnmc.prepare_issuance") }}
        </button>
        <select
          v-if="context.connections.length > 1"
          v-model="connectionId"
          :aria-label="$t('cnmc.connection')"
          class="form-select w-auto"
        >
          <option
            v-for="item in context.connections"
            :key="item.id"
            :value="item.id"
          >
            {{ item.producer || `#${item.id}` }}
          </option>
        </select>
        <span v-if="connection?.producer" class="small text-body-secondary">
          NIF {{ connection.producer }}
        </span>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <button
          class="btn btn-outline-secondary"
          :disabled="!canPrepare"
          @click="showProfile = true"
        >
          {{ $t("cnmc.filing_details") }}
        </button>
      </div>
    </div>
    <CnmcNotice v-if="!canPrepare" class="mb-3">{{
      $t("cnmc.preparation_disabled")
    }}</CnmcNotice>
    <CnmcNotice v-else-if="!connection?.profile" class="mb-3">{{
      $t("cnmc.profile_required")
    }}</CnmcNotice>
    <CnmcNotice
      v-for="[message, templates] in templateNotices"
      :key="message"
      class="mb-3"
    >
      {{ $t(`cnmc.${message}`) }}
      <div class="small mt-1">
        {{ templates.map((template) => template.form).join(" · ") }}
      </div>
    </CnmcNotice>

    <PrepareIssuanceWizard
      v-if="showWizard && connectionId && connection?.profile"
      :companyId
      :connectionId
      :profile="connection.profile"
      :context
      :devices
      :existing="editing"
      :canImport
      @saved="open"
      @cancel="
        showWizard = false;
        refresh();
      "
      @filingDetails="showProfile = true"
    />
    <CnmcDialog
      v-if="selected"
      v-model="showBatch"
      :title="$t('cnmc.batch_number', { id: selected.id })"
      :eyebrow="$t('tradingCenter.tabs.issuance_reporting.title')"
      :busy
      wide
    >
      <div class="p-4">
        <CnmcIssuanceBatchDetail
          v-model:busy="busy"
          :companyId
          :plan="selected"
          :canPrepare
          :canReview="context.can_review"
          :isAdmin
          :showNavigation="false"
          showFilingDetails
          @update="selected = $event"
          @close="showBatch = false"
          @edit="edit"
          @filingDetails="showProfile = true"
        />
      </div>
    </CnmcDialog>
    <div
      v-if="store.loading && !store.plans.length"
      class="py-5 text-center text-body-secondary"
      role="status"
    >
      {{ $t("common.loading") }}
    </div>
    <div
      v-else-if="!store.plans.length"
      class="border rounded-3 p-4 p-md-5 text-center"
    >
      <h5 class="font-serif mb-2">{{ $t("cnmc.no_batches") }}</h5>
      <p class="text-body-secondary mb-0">{{ $t("cnmc.workflow_intro") }}</p>
    </div>
    <div v-else class="table-responsive">
      <table
        class="table table-padded table-hover align-middle cnmc-batches mb-0"
      >
        <thead class="table-light">
          <tr>
            <th>{{ $t("cnmc.production_months") }}</th>
            <th>{{ $t("cnmc.production") }}</th>
            <th>{{ $t("cnmc.domestic_volume") }}</th>
            <th>{{ $t("cnmc.export_volume") }}</th>
            <th>{{ $t("cnmc.progress") }}</th>
            <th>{{ $t("cnmc.next_action") }}</th>
            <th>
              <span class="visually-hidden">{{ $t("cnmc.open_batch") }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="plan in store.plans" :key="plan.id">
            <td>
              <strong>{{ planPeriod(plan, locale) || `#${plan.id}` }}</strong>
              <div class="small text-body-secondary">
                {{ deviceNames(plan) }}
              </div>
            </td>
            <td class="cnmc-batches__quantity">
              {{ $n(reportedProduction(plan), { maximumFractionDigits: 3 }) }}
              MWh
            </td>
            <td>
              <div class="cnmc-batches__quantity">
                {{ $n(plan.domestic_go_count) }} MWh
              </div>
              <div class="small text-body-secondary">
                {{ recipients(plan, "DOMESTIC") }}
              </div>
            </td>
            <td>
              <div class="cnmc-batches__quantity">
                {{ $n(plan.export_go_count) }} MWh
              </div>
              <div class="small text-body-secondary">
                {{ recipients(plan, "EXPORT") }}
              </div>
            </td>
            <td><CnmcPlanProgress :plan /></td>
            <td class="small">
              {{
                $t(`cnmc.actions.${plan.next_action}`, issuanceFileCount(plan))
              }}
            </td>
            <td class="text-end">
              <button
                class="btn btn-outline-primary btn-sm text-nowrap"
                @click="open(plan)"
              >
                {{ $t("cnmc.open_batch") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="small text-body-secondary mt-3 mb-0 px-2">
      {{ $t("cnmc.download_notice") }}
    </p>
    <div class="d-flex justify-content-between mt-3">
      <button
        v-if="offset"
        class="btn btn-outline-secondary btn-sm"
        @click="page(-20)"
      >
        {{ $t("common.back") }}
      </button>
      <button
        v-if="store.hasMore"
        class="btn btn-outline-secondary btn-sm ms-auto"
        @click="page(20)"
      >
        {{ $t("common.next") }}
      </button>
    </div>
    <CnmcFilingDetailsDialog
      v-model="showProfile"
      :profile="connection?.profile ?? null"
      :company
      :account
      :connectionId="connectionId"
      :producerNif="connection?.producer"
      :busy
      @save="saveProfile"
    />
  </div>
</template>

<style scoped lang="scss">
.cnmc-batches {
  min-width: 760px;
  th {
    white-space: nowrap;
  }
  td {
    padding-top: 1rem;
    padding-bottom: 1rem;
    vertical-align: top;
  }
}
.cnmc-batches__quantity {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
